import { DEFAULT_WORKSPACE_SIZE, reindexDesignerSlideNames, buildDesignerSlideName, createSlideId } from './slideModel';
import {
  createDefaultDetailSlideFields,
  createDefaultMainSlideFields,
  reindexTemplateSlideNames,
  resolveImageMetaDisplayName,
} from './templateImageHierarchy';
import { createDesignerObjectId } from '@/image-designer/core/utils/id';
import {
  DEFAULT_PAGE_RATIO_ID,
  createWorkspaceObject,
  getPageSizeForRatio,
} from './pageSizePresets';
import { isStampPreviewLikeObject } from './stampPreviewSync';
import {
  ensureDisplayImageUrls,
  isManagedStorageUrl,
  isSignedDisplayUrl,
  isSignedViewEnabled,
} from '@/utils/imageDisplayAccess';
import { resolveStudioPreviewUrl } from '@/pages/ipe/workbench/utils/studioImagePreview';
import { computeWorkspaceCoverScale } from '@/image-designer/utils/workspaceImageFit';

const TEMPLATE_BASE_ROLE = 'templateBase';

function isDesignerStampArea(obj) {
  return obj?.linkData?.role === 'stampArea' || obj?.stampRole === 'stampArea';
}

function isNonThumbCanvasObject(obj) {
  if (!obj || obj.id === 'workspace') return true;
  return isStampPreviewLikeObject(obj) || isDesignerStampArea(obj);
}

/** 商品主图背景层默认可选中/变换；用户仍可通过图层面板锁定 */
export const TEMPLATE_BASE_LOCK_PROPS = {
  selectable: true,
  evented: true,
  hasControls: true,
  lockMovementX: false,
  lockMovementY: false,
  lockRotation: false,
  lockScalingX: false,
  lockScalingY: false,
};

export function isTemplateBaseObject(obj) {
  return obj?.linkData?.role === TEMPLATE_BASE_ROLE;
}

export function applyTemplateBaseLockProps(obj) {
  if (!obj) return obj;
  Object.assign(obj, TEMPLATE_BASE_LOCK_PROPS);
  return obj;
}

function isPlaceholderTemplateBase(obj) {
  return (
    isTemplateBaseObject(obj)
    && obj.type === 'rect'
    && obj.fill === '#FFFFFF'
    && !obj.strokeWidth
  );
}

function isDirectDesignerFabricSrc(url) {
  const s = String(url || '').trim();
  return s.startsWith('data:') || s.startsWith('blob:');
}

/**
 * 已可写入 fabric 的签名展示地址：
 * - 本地 disk：HMAC `/api/storage/images/view?…`
 * - 线上 OSS：client-direct 预签名直链（Expires/Signature 或 V4 x-oss-*）
 * 二者都算 safe；仅拦截裸 canonical /uploads/ 与未预签名 OSS。
 */
function isSignedDesignerFabricSrc(url) {
  return isSignedDisplayUrl(url);
}

/** fabric Image.src 是否可同步写入（不含 signed-only 下的裸 canonical /uploads/） */
export function isSafeDesignerFabricSrc(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (isDirectDesignerFabricSrc(s)) return true;
  if (isSignedDesignerFabricSrc(s)) return true;
  if (isSignedViewEnabled() && isManagedStorageUrl(s)) return false;
  return true;
}

function getTemplateBaseSrcFromFabric(fabricJson) {
  if (!fabricJson?.objects) return null;
  const base = fabricJson.objects.find((o) => isTemplateBaseObject(o));
  if (base?.type === 'image' && base.src) return base.src;
  const productImage = fabricJson.objects.find(
    (o) => o.type === 'image' && !isNonThumbCanvasObject(o),
  );
  return productImage?.src || null;
}

/** 优先 fabric 已签名 src；signed-only 下禁止回退 slide 元数据裸链 */
function pickDesignerBackgroundSrc(slide, fabricJson) {
  const fromFabric = getTemplateBaseSrcFromFabric(fabricJson);
  const fromSlide = slide?.imageUrl || slide?.thumbUrl;
  for (const candidate of [fromFabric, fromSlide]) {
    if (candidate && isSafeDesignerFabricSrc(candidate)) return candidate;
  }
  if (isSignedViewEnabled()) return '';
  return fromFabric || fromSlide || null;
}

function sanitizeFabricLayerSrc(src) {
  const s = String(src || '').trim();
  if (!s) return '';
  return isSafeDesignerFabricSrc(s) ? s : '';
}

function cloneFabricJson(json) {
  return JSON.parse(JSON.stringify(json || { version: '5.3.0', objects: [] }));
}

export function findTemplateBaseInFabricJson(fabricJson) {
  return (fabricJson?.objects || []).find(
    (obj) => obj.id !== 'workspace' && isTemplateBaseObject(obj),
  );
}

function insertTemplateBaseObject(objects, workspace, baseLayer) {
  const workspaceIndex = objects.findIndex((obj) => obj.id === 'workspace');
  if (workspaceIndex >= 0) {
    objects.splice(workspaceIndex + 1, 0, baseLayer);
  } else {
    const wsW = workspace?.width || DEFAULT_WORKSPACE_SIZE.width;
    const wsH = workspace?.height || DEFAULT_WORKSPACE_SIZE.height;
    objects.unshift(createWorkspaceObject(wsW, wsH), baseLayer);
  }
}

function createPlaceholderTemplateBaseLayer(slide, workspace, wsW, wsH, slideName) {
  return applyTemplateBaseLockProps({
    type: 'rect',
    id: slide?.id || createDesignerObjectId(),
    name: slideName,
    left: workspace?.left ?? 0,
    top: workspace?.top ?? 0,
    originX: workspace?.originX || 'left',
    originY: workspace?.originY || 'top',
    width: wsW,
    height: wsH,
    fill: '#FFFFFF',
    strokeWidth: 0,
    linkData: { role: TEMPLATE_BASE_ROLE },
  });
}

function buildTemplateBaseImageLayer(slide, workspace, wsW, wsH, slideName, src) {
  const safeSrc = sanitizeFabricLayerSrc(src);
  const imgW = slide?.width || wsW;
  const imgH = slide?.height || wsH;
  // cover 铺满画布，避免比例不一致时两侧留白合成 JPG 变白边
  const scale = computeWorkspaceCoverScale(imgW, imgH, wsW, wsH);
  const isRemoteHttp = /^https?:\/\//i.test(safeSrc);
  return applyTemplateBaseLockProps({
    type: 'image',
    id: slide?.id || createDesignerObjectId(),
    name: slideName,
    src: safeSrc,
    ...(isRemoteHttp ? { crossOrigin: 'anonymous' } : {}),
    left: wsW / 2,
    top: wsH / 2,
    originX: 'center',
    originY: 'center',
    width: imgW,
    height: imgH,
    scaleX: scale,
    scaleY: scale,
    linkData: { role: TEMPLATE_BASE_ROLE },
  });
}

/**
 * 确保每个 designer 页在 fabric JSON 中有对应的背景图层（templateBase）。
 * 初始仅 workspace 时，用 slide 缩略图/原图注入；无图时注入白色占位主图，保证图层面板不为空。
 */
export function ensureDesignerBackgroundLayer(fabricJson, slide) {
  const json = cloneFabricJson(fabricJson);
  const objects = json.objects || [];
  const workspace = objects.find((obj) => obj.id === 'workspace');
  const wsW = workspace?.width || slide?.width || DEFAULT_WORKSPACE_SIZE.width;
  const wsH = workspace?.height || slide?.height || DEFAULT_WORKSPACE_SIZE.height;
  const slideName = slide?.name || buildDesignerSlideName(1);
  const src = pickDesignerBackgroundSrc(slide, json);
  const nonWorkspace = objects.filter((obj) => !isNonThumbCanvasObject(obj));

  let base = findTemplateBaseInFabricJson(json);
  if (base) {
    // signed-only：仅在已有安全替换地址时才改写裸链；切勿清空，否则后续 sign 丢失 canonical
    if (
      base.type === 'image'
      && base.src
      && !isSafeDesignerFabricSrc(base.src)
      && src
      && isSafeDesignerFabricSrc(src)
    ) {
      base.src = src;
      if (/^https?:\/\//i.test(src)) {
        base.crossOrigin = 'anonymous';
      }
    }
    if (src && isPlaceholderTemplateBase(base)) {
      const baseIndex = objects.indexOf(base);
      if (baseIndex >= 0) {
        objects.splice(baseIndex, 1, buildTemplateBaseImageLayer(slide, workspace, wsW, wsH, slideName, src));
        json.objects = objects;
        return json;
      }
    }

    base.name = slideName;
    base.linkData = { ...(base.linkData || {}), role: TEMPLATE_BASE_ROLE };
    if (src && base.type === 'image' && !base.src) {
      base.src = src;
      if (/^https?:\/\//i.test(src)) {
        base.crossOrigin = 'anonymous';
      }
    }
    applyTemplateBaseLockProps(base);
    json.objects = objects;
    return json;
  }

  if (!src) {
    if (nonWorkspace.length === 0) {
      insertTemplateBaseObject(
        objects,
        workspace,
        createPlaceholderTemplateBaseLayer(slide, workspace, wsW, wsH, slideName),
      );
      json.objects = objects;
    }
    return json;
  }

  const matchingImage = nonWorkspace.find(
    (obj) => obj.type === 'image' && obj.src && obj.src === src,
  );
  if (matchingImage) {
    matchingImage.name = slideName;
    matchingImage.linkData = { ...(matchingImage.linkData || {}), role: TEMPLATE_BASE_ROLE };
    applyTemplateBaseLockProps(matchingImage);
    json.objects = objects;
    return json;
  }

  const firstImage = nonWorkspace.find((obj) => obj.type === 'image');
  if (firstImage) {
    firstImage.name = slideName;
    firstImage.linkData = { ...(firstImage.linkData || {}), role: TEMPLATE_BASE_ROLE };
    applyTemplateBaseLockProps(firstImage);
    json.objects = objects;
    return json;
  }

  insertTemplateBaseObject(
    objects,
    workspace,
    buildTemplateBaseImageLayer(slide, workspace, wsW, wsH, slideName, src),
  );
  json.objects = objects;
  return json;
}

export function prepareSlideForEditor(slide) {
  if (!slide) return slide;
  const productThumb = resolveSlideProductThumbUrl(slide);
  const enriched = {
    ...slide,
    thumbUrl: productThumb || slide.thumbUrl,
    imageUrl: slide.imageUrl || productThumb,
  };
  return {
    ...enriched,
    fabricCanvas: ensureDesignerBackgroundLayer(enriched.fabricCanvas, enriched),
  };
}

/** loadJSON 前签名 fabric image.src / 印花 exampleImageUrl（resolveSlideCanvasesDisplayUrls 的单页封装） */
export async function prepareSlideForEditorAsync(slide, recordById = {}) {
  if (!slide) return slide;
  const productThumb = resolveSlideProductThumbUrl(slide);
  const rawMeta = slide.imageUrl || productThumb || slide.thumbUrl;
  let displayMeta = rawMeta;
  if (rawMeta && !isDirectDesignerFabricSrc(rawMeta)) {
    displayMeta = await resolveFabricObjectDisplayUrl(rawMeta, recordById[slide.id] || {});
  }
  const enriched = {
    ...slide,
    thumbUrl: displayMeta || slide.thumbUrl,
    imageUrl: displayMeta || slide.imageUrl || productThumb,
  };
  const withBackground = ensureDesignerBackgroundLayer(enriched.fabricCanvas, enriched);
  const signed = await resolveSlideCanvasesDisplayUrls({ [slide.id]: withBackground }, recordById);
  return {
    ...enriched,
    fabricCanvas: signed[slide.id] || withBackground,
  };
}

export function getThumbUrlFromFabricCanvas(fabricJson) {
  if (!fabricJson?.objects) return null;
  const base = fabricJson.objects.find((o) => isTemplateBaseObject(o));
  if (base?.type === 'image' && base.src) return base.src;

  const productImage = fabricJson.objects.find(
    (o) => o.type === 'image' && !isNonThumbCanvasObject(o),
  );
  return productImage?.src || null;
}

/** 商品主图缩略图：优先 slide 元数据，其次 fabric 中的 templateBase */
export function resolveSlideProductThumbUrl(slide, fabricJson = slide?.fabricCanvas) {
  const fromCanvas = getThumbUrlFromFabricCanvas(fabricJson);
  if (fromCanvas) return fromCanvas;
  return slide?.imageUrl || slide?.thumbUrl || null;
}

/** 加载幻灯片后选中商品主图背景层，便于画布与图层面板联动 */
export function selectDefaultSlideLayer(canvas) {
  if (!canvas) return null;
  const objects = canvas.getObjects?.() || [];
  const templateBase = objects.find((obj) => isTemplateBaseObject(obj));
  const firstLayer = objects.find((obj) => obj.id !== 'workspace');
  const target = templateBase || firstLayer;
  if (!target) return null;

  if (templateBase) {
    applyTemplateBaseLockProps(templateBase);
    templateBase.setCoords?.();
  }

  if (!canvas.contextContainer) return target;

  canvas.setActiveObject(target);
  canvas.requestRenderAll();
  return target;
}

export function slideHasMinimumLayer(fabricCanvas) {
  if (!fabricCanvas?.objects) return false;
  return fabricCanvas.objects.some((obj) => obj.id !== 'workspace');
}

export async function measureImageSrc(src) {
  if (!src) return { ...DEFAULT_WORKSPACE_SIZE };
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || DEFAULT_WORKSPACE_SIZE.width,
        height: img.naturalHeight || DEFAULT_WORKSPACE_SIZE.height,
      });
    };
    img.onerror = () => resolve({ ...DEFAULT_WORKSPACE_SIZE });
    img.src = src;
  });
}

/**
 * 为单张模板图生成 fabric JSON（页面尺寸由比例预设决定，图片等比 cover 铺满画布）
 */
export function buildFabricCanvasJsonForImage(imageMeta, pageSize) {
  const w = pageSize?.width || DEFAULT_WORKSPACE_SIZE.width;
  const h = pageSize?.height || DEFAULT_WORKSPACE_SIZE.height;
  const src = imageMeta.src || imageMeta.imageUrl || imageMeta.thumbUrl;
  const imgW = imageMeta.width || w;
  const imgH = imageMeta.height || h;

  const objects = [createWorkspaceObject(w, h)];

  if (src) {
    const safeSrc = sanitizeFabricLayerSrc(src);
    const scale = computeWorkspaceCoverScale(imgW, imgH, w, h);
    const layerName = imageMeta.name || imageMeta.designerName || buildDesignerSlideName(1);
    const isRemoteHttp = /^https?:\/\//i.test(safeSrc);
    objects.push(applyTemplateBaseLockProps({
      type: 'image',
      id: imageMeta.id || createDesignerObjectId(),
      name: layerName,
      src: safeSrc,
      ...(isRemoteHttp ? { crossOrigin: 'anonymous' } : {}),
      left: w / 2,
      top: h / 2,
      originX: 'center',
      originY: 'center',
      width: imgW,
      height: imgH,
      scaleX: scale,
      scaleY: scale,
      linkData: { role: TEMPLATE_BASE_ROLE },
    }));
  }

  return {
    version: '5.3.0',
    objects,
  };
}

export function buildSlideFromImageMeta(imageMeta, pageSize) {
  const w = pageSize?.width || imageMeta.width || DEFAULT_WORKSPACE_SIZE.width;
  const h = pageSize?.height || imageMeta.height || DEFAULT_WORKSPACE_SIZE.height;
  const src = imageMeta.src || imageMeta.imageUrl || imageMeta.thumbUrl;
  const ratioId = pageSize?.ratioId || DEFAULT_PAGE_RATIO_ID;

  return {
    id: imageMeta.id,
    name: resolveImageMetaDisplayName(imageMeta, buildDesignerSlideName(1)),
    thumbUrl: src,
    imageUrl: src,
    width: imageMeta.width || w,
    height: imageMeta.height || h,
    pageRatio: ratioId,
    fabricCanvas: buildFabricCanvasJsonForImage({ ...imageMeta, src }, { width: w, height: h }),
  };
}

export async function resolveEntryImages(rawImages = []) {
  const { getImageFromIndexedDB } = await import('@/utils/imageStorage');
  const workItems = [];

  for (let i = 0; i < rawImages.length; i += 1) {
    const img = rawImages[i];
    if (!img) continue;

    let src = img.src || img.url || img.imageUrl || img.thumbUrl;
    const id = img.id || `img-${Date.now()}-${i}`;

    if (!src && id) {
      try {
        src = await getImageFromIndexedDB(id);
      } catch {
        /* ignore */
      }
    }

    if (!src) continue;
    workItems.push({ img, id, src });
  }

  const batchUrls = workItems
    .map((item) => item.src)
    .filter((src) => src && !src.startsWith('data:') && !src.startsWith('blob:'));
  if (batchUrls.length > 0) {
    await ensureDisplayImageUrls(batchUrls);
  }

  const resolved = [];
  for (const { img, id, src } of workItems) {
    let displaySrc = String(src || '').trim();
    if (displaySrc && !displaySrc.startsWith('data:') && !displaySrc.startsWith('blob:')) {
      displaySrc = await resolveStudioPreviewUrl(displaySrc, img);
      if (!displaySrc) continue;
    }

    let width = img.width;
    let height = img.height;
    if (!width || !height) {
      const measured = await measureImageSrc(displaySrc);
      width = measured.width;
      height = measured.height;
    }

    resolved.push({
      id,
      name: resolveImageMetaDisplayName(img, `图片 ${resolved.length + 1}`),
      src: displaySrc,
      thumbUrl: displaySrc,
      imageUrl: displaySrc,
      width,
      height,
    });
  }

  return resolved;
}

function collectFabricCanvasManagedUrls(canvas) {
  const rawUrls = [];
  if (!canvas?.objects) return rawUrls;

  canvas.objects.forEach((obj) => {
    const src = String(obj?.src || '').trim();
    if (src && !src.startsWith('data:') && !src.startsWith('blob:')) {
      rawUrls.push(src);
    }
    if (!isDesignerStampArea(obj)) return;
    const exampleUrl = String(obj.linkData?.exampleImageUrl || '').trim();
    if (exampleUrl && !exampleUrl.startsWith('data:') && !exampleUrl.startsWith('blob:')) {
      rawUrls.push(exampleUrl);
    }
  });
  return rawUrls;
}

async function resolveFabricObjectDisplayUrl(raw, meta = {}) {
  const trimmed = String(raw || '').trim();
  if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }
  return resolveStudioPreviewUrl(trimmed, meta);
}

/** 为已保存的 slideCanvases 内 image.src / 印花示例图 URL 批量签名（模板 reload 走 fabric JSON 时使用） */
export async function resolveSlideCanvasesDisplayUrls(slideCanvases, recordById = {}) {
  if (!slideCanvases || typeof slideCanvases !== 'object') return slideCanvases;

  const rawUrls = [
    ...new Set(
      Object.values(slideCanvases).flatMap((canvas) => collectFabricCanvasManagedUrls(canvas)),
    ),
  ];
  if (rawUrls.length > 0) {
    await ensureDisplayImageUrls(rawUrls);
  }

  const out = {};
  for (const [slideId, canvas] of Object.entries(slideCanvases)) {
    const cloned = JSON.parse(JSON.stringify(canvas));
    if (Array.isArray(cloned.objects)) {
      for (const obj of cloned.objects) {
        if (obj?.type === 'image') {
          const raw = String(obj.src || '').trim();
          if (raw && !raw.startsWith('data:') && !raw.startsWith('blob:')) {
            const meta = recordById[obj.id] || recordById[slideId] || {};
            const next = await resolveFabricObjectDisplayUrl(raw, meta);
            // 签名失败勿用空串覆盖，否则丢失 canonical，无法重试
            if (next) obj.src = next;
          }
        }
        if (isDesignerStampArea(obj) && obj.linkData?.exampleImageUrl) {
          const raw = String(obj.linkData.exampleImageUrl || '').trim();
          if (raw && !raw.startsWith('data:') && !raw.startsWith('blob:')) {
            const linkData = obj.linkData || {};
            const nextExample = await resolveFabricObjectDisplayUrl(raw, {
              displayUrl: linkData.exampleImageDisplayUrl,
              viewUrl: linkData.exampleImageViewUrl,
            });
            if (nextExample) {
              obj.linkData = {
                ...linkData,
                exampleImageUrl: nextExample,
              };
            }
          }
        }
      }
    }
    out[slideId] = cloned;
  }
  return out;
}

export async function buildBootstrapFromEntryImages(rawImages = [], options = {}) {
  const images = await resolveEntryImages(rawImages);
  if (images.length === 0) {
    return null;
  }

  const pageSize = getPageSizeForRatio(options.pageRatioId || DEFAULT_PAGE_RATIO_ID);
  const slides = reindexDesignerSlideNames(
    images.map((img) => buildSlideFromImageMeta(img, pageSize)),
  );
  const activeSlideId = slides[0].id;

  return {
    slides,
    activeSlideId,
    primaryImageId: activeSlideId,
  };
}

/**
 * 从「颜色 + 主图 + 细节图」槽位构建设计器 bootstrap（新建套图模板弹窗）
 */
export async function buildBootstrapFromColorSlots(colorSlots = [], options = {}) {
  const slots = Array.isArray(colorSlots) ? colorSlots : [];
  if (!slots.length) return null;

  const pageSize = getPageSizeForRatio(options.pageRatioId || DEFAULT_PAGE_RATIO_ID);
  const slides = [];

  for (const slot of slots) {
    const mainList = await resolveEntryImages([slot?.main].filter(Boolean));
    const mainMeta = mainList[0];
    if (!mainMeta?.src && !mainMeta?.imageUrl && !mainMeta?.thumbUrl) continue;

    const mainId = mainMeta.id || createSlideId();
    const mainSrc = mainMeta.src || mainMeta.imageUrl || mainMeta.thumbUrl;
    const mainName = resolveImageMetaDisplayName(mainMeta);
    const mainSlide = buildSlideFromImageMeta(
      {
        ...mainMeta,
        id: mainId,
        src: mainSrc,
        thumbUrl: mainSrc,
        imageUrl: mainSrc,
        name: mainName,
      },
      pageSize,
    );
    slides.push({
      ...mainSlide,
      id: mainId,
      name: mainName || mainSlide.name,
      ...createDefaultMainSlideFields(),
      colorLabel: String(slot.colorLabel || '').trim(),
      colorCode: String(slot.colorCode || '').trim(),
    });

    const detailMetas = await resolveEntryImages(
      (Array.isArray(slot.details) ? slot.details : []).filter((row) => row?.src || row?.imageUrl),
    );
    detailMetas.forEach((detailMeta) => {
      const detailId = detailMeta.id || createSlideId();
      const detailSrc = detailMeta.src || detailMeta.imageUrl || detailMeta.thumbUrl;
      const detailName = resolveImageMetaDisplayName(detailMeta);
      const detailSlide = buildSlideFromImageMeta(
        {
          ...detailMeta,
          id: detailId,
          src: detailSrc,
          thumbUrl: detailSrc,
          imageUrl: detailSrc,
          name: detailName,
        },
        pageSize,
      );
      slides.push({
        ...detailSlide,
        id: detailId,
        name: detailName || detailSlide.name,
        ...createDefaultDetailSlideFields(mainId),
      });
    });
  }

  if (!slides.length) return null;
  const normalized = reindexTemplateSlideNames(slides);
  const activeSlideId = normalized.find((slide) => slide.role !== 'detail')?.id || normalized[0]?.id;
  return {
    slides: normalized,
    activeSlideId,
    primaryImageId: activeSlideId,
  };
}

export function attachThumbToSlides(slides, imagesMeta = []) {
  return slides.map((slide) => {
    const meta = imagesMeta.find((m) => m.id === slide.id);
    const thumbUrl =
      resolveSlideProductThumbUrl(slide)
      || meta?.imageUrl
      || meta?.thumbUrl
      || meta?.src
      || null;

    return {
      ...slide,
      thumbUrl,
      imageUrl: slide.imageUrl || meta?.imageUrl || thumbUrl,
      width: slide.width || meta?.width,
      height: slide.height || meta?.height,
      name: slide.name,
    };
  });
}

/** 复制幻灯片时重映射 fabric 内对象 id，避免与源页冲突 */
export function remapFabricCanvasForSlide(fabricCanvas, newSlideId, sourceSlideId) {
  if (!fabricCanvas?.objects) return fabricCanvas;
  const cloned = JSON.parse(JSON.stringify(fabricCanvas));
  cloned.objects = cloned.objects.map((obj) => {
    if (obj.id === 'workspace') return obj;
    const next = { ...obj };
    if (
      isTemplateBaseObject(next)
      || (next.type === 'image' && sourceSlideId && next.id === sourceSlideId)
    ) {
      next.id = newSlideId;
      return next;
    }
    if (next.linkData?.role === 'stampArea' || next.stampRole === 'stampArea') {
      next.id = `stamp-${newSlideId}-${createDesignerObjectId().slice(-8)}`;
      return next;
    }
    if (next.id) {
      next.id = createDesignerObjectId();
    }
    return next;
  });
  return cloned;
}

export function buildDuplicateSlideName(sourceName, existingNames = []) {
  const base = String(sourceName || '页面').replace(/\s*副本\d*$/u, '').trim() || '页面';
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const copyPattern = new RegExp(`^${escaped}\\s+副本(\\d+)?$`);

  let maxSuffix = 0;
  existingNames.forEach((name) => {
    const trimmed = String(name).trim();
    if (trimmed === base) {
      maxSuffix = Math.max(maxSuffix, 0);
      return;
    }
    const match = copyPattern.exec(trimmed);
    if (match) {
      const n = match[1] ? Number(match[1]) : 1;
      maxSuffix = Math.max(maxSuffix, n);
    }
  });

  return maxSuffix === 0 ? `${base} 副本` : `${base} 副本${maxSuffix + 1}`;
}

export function slideHasDesignContent(fabricCanvas) {
  if (!fabricCanvas?.objects) return false;
  return fabricCanvas.objects.some(
    (obj) =>
      obj.id !== 'workspace'
      && (obj.linkData?.role === 'stampArea'
        || obj.stampRole === 'stampArea'
        || isTemplateBaseObject(obj)
        || obj.type === 'image'),
  );
}
